import { observable, action, computed, toJS } from 'mobx';
import { getCustomer, updateCustomer, getCustomers, createCustomer, syncCustomers } from '../api/customerApi';
import * as typeApi from '../api/typeApi';
import { fromPromise } from 'mobx-utils';
import { createConfig, updateConfig, createConfigItem, deleteConfigItem } from '../api/aggregatorConfigApi';
import * as configApi from '../api/aggregatorConfigApi'
import { createPolarisConfig, updatePolarisConfig, syncPolaris } from '../api/polarisApi';
import * as branchApi from '../api/branchApi';
import * as communicationPreferencesApi from '../api/communicationPreferencesApi';
import NotificationType from '../models/NotificationType';
import PolarisConfig from '../models/PolarisConfig';

class ConfigurationStore {
  @observable request = {};
  @observable customers = [];
  @observable customer = {};
  @observable customerUpdated = false;
  @observable configUpdated = false;
  @observable notificationChannels = [];
  @observable notificationTypes = [];
  @observable notificationType = {};
  @observable blockedNotificationTypes = null;
  @observable branches = [];
  @observable branchConfigItems = [];
  @observable currentCustomerId = null;
  @observable currentConfigId = null;
  @observable alertMessage = {
    style: '',
    message: '',
    show: false
  };

  constructor(rootStore) {
    this.rootStore = rootStore;
  }

  @computed
  get isLoading() {
    return this.request.state === void 0 || this.request.state === 'pending';
  }

  @computed
  get config() {
    if (this.customer.configs === void 0)
      return;

    return this.customer.configs.find(config => config.id === this.currentConfigId);
  }
  @computed
  get hasBlankItem() {
    return this.config.configItems.find(item => item.propertyName === '') !== void 0;
  }

  @computed
  get currentPolarisConfig() {
    if (this.customerHasPolarisConfig)
      return PolarisConfig.create(toJS(this.customer.polarisConfig));

    return null;
  }

  @computed
  get customerHasPolarisConfig() {
    return this.customer && this.customer.polarisConfig;
  }

  @action
  getCustomers() {
    this.request = fromPromise(getCustomers());
    this.request.then(customers => this.customers = customers);
  }

  @action.bound
  setCurrentCustomerId(id) {
    if (this.currentCustomerId === id)
      return;

    this.currentCustomerId = id;
    this.request = fromPromise(getCustomer(id));
    this.request.then(customer => this.customer = customer);
  }

  @action.bound
  setCurrentConfigId(customerId, configId) {
    if (this.currentCustomerId === customerId && this.currentConfigId === configId)
      return;

    if (this.currentCustomerId !== customerId || this.customer.id === void 0)
      this.setCurrentCustomerId(customerId);

    this.currentConfigId = configId;
  }

  @action.bound
  createCustomer(customer) {
    createCustomer(customer).then(this.addCustomerToCustomers);
  }

  @action.bound
  updateCustomer(customer) {
    this.resetCustomerUpdated();
    customer.id = this.currentCustomerId;
    updateCustomer(customer).then(action(() => {
      this.customerUpdated = true;
      this.customer = customer;
    }));
  }

  @action.bound
  resetCustomerUpdated() {
    this.customerUpdated = false;
    this.hideAlert();
  }

  @action.bound
  setAlert(msg, style) {
    this.alertMessage = {
      style: style,
      message: msg,
      show: true
    }
    if (style !== 'danger') {
      setTimeout(() => {
        this.hideAlert();
      }, 3000);
    }
  }

  @action.bound
  hideAlert() {
    this.alertMessage = {
      style: '',
      message: '',
      show: false
    }
  }

  @action.bound
  createConfig(config, onSuccess) {
    config.customerId = this.currentCustomerId;
    createConfig(config)
      .then(response => {
        if (response.ok) {
          const addConfig = config.isCopy ? this.addConfigAndItemsToCustomer : this.addConfigToCustomer;
          response.json().then(addConfig);
          this.setAlert('Config successfully created!', 'success')
          onSuccess && onSuccess();
          return;
        }
        response.json()
          .then(action((errorDetails) => {
            if (errorDetails === undefined || !errorDetails.Message)
              return;

            this.setAlert(
              `Oops! We were unable to process this request. \nError details:\n${errorDetails.Message}`,
              'danger'
            );
          }));
      })
      .catch(console.error)
  }

  @action.bound
  resetConfigUpdated() {
    this.configUpdated = false;
  }

  @action.bound
  updateConfig(config = {}) {
    const configToUpdate = Object.assign({}, this.config, config);
    updateConfig(toJS(configToUpdate)).then(response => {
      if (response.ok) {
        response.json();
        Object.assign(this.config, config);
        this.setAlert('Successfully Updated!', 'success');
        return;
      }
      response.json()
        .then(action((errorDetails) => {
          if (errorDetails === undefined || !errorDetails.Message)
            return;

          this.setAlert(
            `Oops! We were unable to process this request. \nError details:\n${errorDetails.Message}`,
            'danger'
          );
          return;
        }));

      this.configUpdated = true;
    })
      .catch(console.error);
  }

  @action.bound
  saveAllConfig() {
    const currentConfig = toJS(this.config);
    const unsavedItems = currentConfig.configItems.filter(item => item.unsaved);
    currentConfig.configItems = currentConfig.configItems.filter(item => !item.unsaved || item.unsaved === void 0);
    updateConfig(currentConfig)
      .then(() => unsavedItems.forEach(item => {
        createConfigItem(this.currentConfigId, item)
          .then(() => this.saveConfigItem(item));
      }));
  }

  @action.bound
  saveConfigItem(item) {
    const configItem = this.config.configItems.find(i => i.propertyName === item.propertyName);
    configItem.unsaved = false;
  }

  @action.bound
  addConfigItemToConfig(configItem) {
    configItem.aggregatorConfigId = this.currentConfigId;
    this.config.push(configItem);
    updateConfig(this.config).then(c => this.config = c);
  }

  @action.bound
  addConfigToCustomer(config) {
    if (config)
      this.customer.configs.push(config);
  }

  @action.bound
  addConfigAndItemsToCustomer(config) {
    if (config) {
      this.customer.configs.push(config);
      this.copyItemsToConfig(config.id);
    }
  }

  @action.bound
  addCustomerToCustomers(customer) {
    this.customers.push(customer);
  }

  @action.bound
  getNotificationChannels() {
    if (this.notificationChannels.length !== 0)
      return;

    typeApi.getNotificationChannels()
      .then(action(channels => this.notificationChannels = channels));
  }

  @action.bound
  getNotificationTypes() {
    typeApi.getNotificationTypes()
      .then(action(types => this.notificationTypes = types.map(type => NotificationType.create(type))));
  }

  @action.bound
  getCurrentNotificationType(typeIdFromState) {
    const typeId = typeIdFromState === 0 ? this.config.typeId : typeIdFromState;
    return this.notificationTypes.find(type => type.id === parseInt(typeId));
  }

  @action.bound
  setCurrentNotificationId(id) {
    if (this.currentNotificationId === id)
      return;

    this.currentNotificationId = id;
    const request = typeApi.getNotificationType(id).then(action(notificationType => {
      this.notificationType = NotificationType.create(notificationType)
    }));
    this.request = fromPromise(request);
  }

  @action.bound
  createNotificationType(notificationTypeName) {
    const notificationType = {
      id: 0,
      name: notificationTypeName
    };
    typeApi.createNotificationType(notificationType).then(this.addNotificationTypeToNotificationTypes);
  }

  @action.bound
  updateNotificationType() {
    typeApi.updateNotificationType(this.notificationType)
      .then(notificationType => this.notificationType = NotificationType.create(notificationType));
  }

  @action.bound
  addNotificationTypeToNotificationTypes(notificationType) {
    this.notificationTypes.push(NotificationType.create(notificationType));
  }

  @action.bound
  updateConfigItem(propertyName, name, value) {
    const configItem = this.config.configItems.find(item => item.propertyName === propertyName);
    configItem[name] = value;
  }

  @action.bound
  addCustomItem() {
    this.addConfigItem(true);
  }

  @action.bound
  addDefinedItem() {
    this.addConfigItem(false);
  }

  @action
  addConfigItem(isCustomProperty) {
    const newConfigItem = {
      configId: this.currentConfigId,
      propertyName: '',
      propertyMappingValue: '',
      staticPropertyValue: '',
      isStaticProperty: false,
      isCustomProperty,
      unsaved: true
    };

    this.config.configItems.push(newConfigItem);
  }

  @action.bound
  deleteConfigItem(propertyName) {
    const configItem = this.config.configItems.find(item => item.propertyName === propertyName);

    if (configItem.unsaved)
      return this.removeItemFromConfig(propertyName)

    deleteConfigItem(this.currentConfigId, propertyName)
      .then(() => {
        this.removeItemFromConfig(propertyName);
      });
  }

  @action.bound
  removeItemFromConfig(propertyName) {
    this.config.configItems = this.config.configItems.filter(item => item.propertyName !== propertyName);
  }

  @action.bound
  copyItemsToConfig(newId) {
    const itemsClone = Object.assign({}, this.config.configItems);
    this.currentConfigId = newId;
    Object.keys(itemsClone).forEach(item => {
      createConfigItem(this.currentConfigId, itemsClone[item])
      this.config.configItems.push(itemsClone[item]);
    });
  }

  @action.bound
  createPolarisConfig(config, onSuccess) {
    createPolarisConfig(this.currentCustomerId, config).then(response => {
      if (response.ok) {
        response.json().then(action(result => {
          this.customer.polarisConfig = result;
          this.setAlert(
            'Successfully Created Polaris Config!',
            'success'
          );
          onSuccess && onSuccess();
        }));
        return;
      }
      response.json()
        .then(action((errorDetails) => {
          let message = '';
          if (errorDetails !== undefined && !errorDetails.Message)
            message = errorDetails.Message;

          this.setAlert(
            `Oops! We were unable to process this request. \nError details:\n${message}`,
            'danger'
          );
          return;
        }));
    });
  }

  @action.bound
  updatePolarisConfig(config, onSuccess) {
    updatePolarisConfig(this.currentCustomerId, config).then(response => {
      if (response.ok) {
        response.json().then(action(result => {
          this.customer.polarisConfig = result;
          this.setAlert(
            'Successfully Updated Polaris Config!',
            'success'
          );
          onSuccess && onSuccess();
        }));
        return;
      }
      response.json()
        .then(action((errorDetails) => {
          if (errorDetails === undefined || !errorDetails.Message)
            return;

          this.setAlert(
            `Oops! We were unable to process this request. \nError details:\n${errorDetails.Message}`,
            'danger'
          );
          return;
        }));
    });
  }

  @action.bound
  getBranches() {
    branchApi.getBranches(this.currentCustomerId)
      .then(action(branches => this.branches = branches.map(b => observable(b))));
  }

  @action.bound
  addNewBranch() {
    this.branches.push({ id: 0, name: '', branchRefId: '0' })
  }

  @computed
  get hasNewBranch() {
    return this.branches.find(item => item.id == 0) != null;
  }

  @action.bound
  updateBranchItem(branchId, name, value) {
    const branchItem = this.branches.find(item => item.id == branchId);
    branchItem[name] = value;
  }

  @action.bound
  saveBranchItem(branchId) {
    const branchItem = this.branches.find(item => item.id == branchId);
    if (branchItem.id == "0") {
      branchApi.saveBranchItem(this.currentCustomerId, branchItem).then(action(response => {
        if (response.ok) {
          response.json().then(action(result => {
            branchItem.id = result.id;
            this.setAlert(
              'Successfully Saved Item!',
              'success'
            );
          }));
          return;
        }
        response.json()
          .then(action((errorDetails) => {
            if (errorDetails === undefined || !errorDetails.Message)
              return;

            this.setAlert(
              `Oops! We were unable to process this request. \nError details:\n${errorDetails.Message}`,
              'danger'
            );
            return;
          }));
      }));
    } else {
      branchApi.updateBranchItem(this.currentCustomerId, branchItem).then(action(response => {
        if (response.ok) {
          response.json().then(action(result => {
            this.setAlert(
              'Successfully Saved Item!',
              'success'
            );
          }));
          return;
        }
        response.json()
          .then(action((errorDetails) => {
            if (errorDetails === undefined || !errorDetails.Message)
              return;

            this.setAlert(
              `Oops! We were unable to process this request. \nError details:\n${errorDetails.Message}`,
              'danger'
            );
            return;
          }));
      }));
    }
  }


  @computed
  get hasBlankBranchItem() {
    return this.branchConfigItems.find(item => item.propertyName === '') !== void 0;
  }

  @action.bound
  getBranchItems(configId, branchId) {
    if (branchId == null || branchId == '0') {
      this.branchConfigItems = []
      return;
    }

    configApi.getBranchConfigItems(configId, branchId)
      .then(action(items => this.branchConfigItems = items.map(b => observable(b))));
  }

  @action.bound
  updateBranchConfigItem(propertyName, name, value) {
    const configItem = this.branchConfigItems.find(item => item.propertyName === propertyName);

    if (name == 'propertyName' && value != '') {
      const { configId, branchId, unsaved } = configItem
      const originalConfigItem = this.config.configItems.find(item => item.propertyName === value);
      Object.assign(configItem, toJS(originalConfigItem))
      configItem.configId = configId;
      configItem.branchId = branchId;
      configItem.unsaved = unsaved
    } else {
      configItem[name] = value;
    }
  }

  @action.bound
  deleteBranchConfigItem(configId, branchId, propertyName) {
    const configItem = this.branchConfigItems.find(item => item.propertyName === propertyName);

    if (configItem.unsaved) {
      this.branchConfigItems = this.branchConfigItems.filter(item => item.propertyName !== propertyName)
      return
    }


    configApi.deleteBranchConfigItem(configId, branchId, propertyName)
      .then(
        action(() => this.branchConfigItems = this.branchConfigItems.filter(item => item.propertyName !== propertyName)
        ));
  }

  @action.bound
  addBranchConfigItem(configId, branchId, isCustomProperty) {
    const newConfigItem = observable({
      configId: configId,
      branchId: branchId,
      propertyName: '',
      propertyMappingValue: '',
      staticPropertyValue: '',
      isStaticProperty: false,
      isCustomProperty,
      unsaved: true
    });

    this.branchConfigItems.push(newConfigItem);
  }

  @action.bound
  saveAllBranchItems() {
    const itemsToSave = this.branchConfigItems.filter(item => item.unsaved);
    const itemsToUpdate = this.branchConfigItems.filter(item => !item.unsaved || item.unsaved === void 0);
    const savePromises = itemsToSave.map(item => configApi.createBranchConfigItem(item.configId, item.branchId, item))
    const updatePromises = itemsToUpdate.map(item => configApi.updateBranchConfigItem(item.configId, item.branchId, item))

    Promise.all(savePromises.concat(updatePromises)).then(action(response => {
      this.setAlert(
        'Successfully Saved Items!',
        'success'
      );
      this.branchConfigItems.forEach(item => item.unsaved = false)
    })).catch(action(() => this.setAlert(
      `Oops! We were unable to process this request.`,
      'danger'
    )))
  }

  @action.bound
  getBlockableTypes() {
    this.blockedNotificationTypes = null;
    communicationPreferencesApi.getBlockableTypes(this.currentCustomerId)
      .then(action(types => this.blockedNotificationTypes = types.map(b => observable(b))));
  }

  @action.bound
  blockNotificationType(notificationTypeId) {
    communicationPreferencesApi.blockType(this.currentCustomerId, notificationTypeId)
      .then(action(() => {
        this.setAlert(
          'Successfully Updated Type!',
          'success'
        );
        return;
      }))
      .catch(action(() => {
        this.setAlert(
          `Oops! We were unable to process this request`,
          'danger'
        );
      }))
  }

  @action.bound
  syncPolaris() {
    syncPolaris()
      .then(action(() => {
        this.setAlert(
          'Successfully Published Events!',
          'success'
        );
        return;
      }))
      .catch(action(() => {
        this.setAlert(
          `Oops! We were unable to process this request`,
          'danger'
        );
      }))
  }

  @action.bound
  syncCustomers() {
    syncCustomers()
      .then(action(() => {
        this.setAlert(
          'Successfully Published Events!',
          'success'
        );
        return;
      }))
      .catch(action(() => {
        this.setAlert(
          `Oops! We were unable to process this request`,
          'danger'
        );
      }))
  }
}

export default ConfigurationStore;